'use client';
import {type AmenityCategory} from '#/cms/schemas/amenity-category.schema';
import {type CategoryClass} from '#/cms/schemas/category-class.schema';
import {type EventCategory} from '#/cms/schemas/event-category.schema';
import {type Event} from '#/cms/schemas/event.schema';
import {type PlaceSummary} from '#/cms/schemas/place-summary.schema';
import useAnalytics, {AnalyticsEventAction, AnalyticsEventCategory} from '#/features/analytics/useAnalytics';
import {EventResult} from '#/features/components/EventResult';
import {IconButton} from '#/features/components/IconButton';
import {PepIcon} from '#/features/components/PepIcon';
import SubmitYourEvent from '#/features/components/SubmitYourEvent';
import {Heading1} from '#/features/components/Text';
import TextSearchBox from '#/features/components/TextSearchBox';
import {usePlaceStore} from '#/features/store/placeStore';
import {useUiStrings} from '#/features/store/uiStrings';
import {useCategoryFilterLogic} from '#/utils/categoryHooks';
import {latLngToGoogleLatLngLiteral, useRadiusOptions, type Radius, type SearchRadius} from '#/utils/geo';
import {
  PANEL_VIEW_SEARCH_PARAM_KEY,
  TEXT_SEARCH_PARAM_KEY,
  useActiveFilters,
  useClearQueryParams,
  usePanelView,
  useURLFilterParams,
  useUpdateQueryParams,
} from '#/utils/queryParams';
import useDevice from '#/utils/useDevice';
import {Dialog, Transition} from '@headlessui/react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import {isPointWithinRadius} from 'geolib';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {EventsFilter} from './EventsFilter';
import {EventsMap} from './EventsMap';

type Props = {
  events: Event[];
  places: PlaceSummary[];
  categoryClasses: CategoryClass[];
  eventCategories: EventCategory[];
  amenityCategories: AmenityCategory[];
};

export const EventsPanel = (props: Props) => {
  const {events, places, categoryClasses, eventCategories, amenityCategories} = props;
  const {registerEvent} = useAnalytics();
  const uiStrings = useUiStrings();
  const activeFilters = useActiveFilters(eventCategories, amenityCategories);
  const categoryFilterLogic = useCategoryFilterLogic();
  const {panelView, togglePanelView} = usePanelView();
  const updateQueryParams = useUpdateQueryParams();
  const clearQueryParams = useClearQueryParams();
  const {placeMap} = usePlaceStore();
  const radiusOptions = useRadiusOptions();
  const initialSearchText = useMemo(
    () => activeFilters.find(filter => filter.id === TEXT_SEARCH_PARAM_KEY)?.label ?? '',
    [activeFilters]
  );
  const [searchText, setSearchText] = useState(initialSearchText);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const {isMobile} = useDevice();
  const {
    startDate,
    endDate,
    categories,
    amenities,
    placeId: selectedPlace,
    radius: selectedRadius,
  } = useURLFilterParams();

  const mapSearchRadius = useMemo(() => {
    if (!selectedPlace || !selectedRadius) return undefined;
    const place = placeMap.get(selectedPlace);
    if (!place?.centroid) return undefined;
    const radiusOption = radiusOptions[selectedRadius as Radius] ?? null;
    if (!radiusOption) return undefined;
    const searchRadius: SearchRadius = {
      radius: radiusOption.radius,
      center: latLngToGoogleLatLngLiteral(place.centroid) ?? {lat: 0, lng: 0},
      title: place.title ? `${place.title} ${radiusOption.title}` : radiusOption.title,
    };
    return searchRadius;
  }, [placeMap, radiusOptions, selectedPlace, selectedRadius]);

  const toggleMobileFilterModal = useCallback(() => {
    setMobileFiltersOpen(v => !v);
  }, []);

  const handleSearchTextChange = useCallback(
    (text: string | null) => {
      setSearchText(text ?? '');
      updateQueryParams({[TEXT_SEARCH_PARAM_KEY]: text});
      registerEvent({
        category: AnalyticsEventCategory.Events,
        action: AnalyticsEventAction.FilterByText,
        label: text ?? '',
      });
    },
    [setSearchText, updateQueryParams, registerEvent]
  );

  const filteredEvents = useMemo(() => {
    let result = events;

    if (searchText.length > 0) {
      const currentSearchText = searchText.toLowerCase();
      result = result.filter(
        event =>
          (event.title ?? event.id).toLowerCase().includes(currentSearchText) ||
          event.description?.toLowerCase().includes(currentSearchText)
      );
    }

    if (startDate) {
      // keep everything that ends after the start date
      result = result.filter(event =>
        event.endDateTime
          ? dayjs(event.endDateTime).isAfter(startDate)
          : dayjs(event.startDateTime).endOf('day').isAfter(startDate)
      );
    }

    if (endDate) {
      // keep everything that starts before the end date
      result = result.filter(event => dayjs(event.startDateTime).isBefore(endDate));
    }

    if (categories.length !== 0) {
      if (categoryFilterLogic.eventCategories === 'or') {
        result = result.filter(event => event.eventCategories?.some(category => categories.includes(category.id)));
      }
      if (categoryFilterLogic.eventCategories === 'and') {
        result = result.filter(event =>
          categories.every(category => event.eventCategories?.some(c => c.id === category))
        );
      }
    }
    if (amenities.length !== 0) {
      if (categoryFilterLogic.amenityCategories === 'or') {
        result = result.filter(event => event.amenityCategories?.some(amenity => amenities.includes(amenity.id)));
      }
      if (categoryFilterLogic.amenityCategories === 'and') {
        result = result.filter(event =>
          amenities.every(amenity => event.amenityCategories?.some(a => a.id === amenity))
        );
      }
    }

    if (selectedPlace && selectedRadius) {
      const place = placeMap.get(selectedPlace);
      const {radius} = radiusOptions[selectedRadius as Radius] ?? {};
      if (place && radius) {
        result = result.filter(event => {
          const isInRadius =
            !!place.centroid && !!event.coordinate && isPointWithinRadius(event.coordinate, place.centroid, radius);
          return !!event.placeIds?.some(placeId => placeId === selectedPlace) || isInRadius;
        });
      }
    }
    return result;
  }, [
    amenities,
    categories,
    categoryFilterLogic.amenityCategories,
    categoryFilterLogic.eventCategories,
    endDate,
    events,
    placeMap,
    radiusOptions,
    searchText,
    selectedPlace,
    selectedRadius,
    startDate,
  ]);

  useEffect(() => {
    if (!isMobile && mobileFiltersOpen) setMobileFiltersOpen(false);
  }, [isMobile, mobileFiltersOpen]);

  return (
    <div className="flex h-full flex-row overflow-hidden mobile:flex-col">
      {/* desktop filters */}
      <div className="desktop-only min-w-[25rem] overflow-y-auto overflow-x-hidden scrollbar md:h-dvh">
        <EventsFilter
          className="desktop-only w-[25rem]"
          events={events}
          places={places}
          categoryClasses={categoryClasses}
          eventCategories={eventCategories}
          amenityCategories={amenityCategories}
          handleSearchTextChange={handleSearchTextChange}
          filteredEvents={filteredEvents}
          searchText={searchText}
          unMountInputs={isMobile}
        />
      </div>

      {/* mobile filters */}
      <Transition
        as={Dialog}
        className="relative z-40"
        show={mobileFiltersOpen}
        onClose={toggleMobileFilterModal}
        aria-labelledBy="filter-panel-title"
      >
        <Transition.Child
          className="fixed inset-0 bg-foreground opacity-50"
          aria-hidden="true"
          enter="duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-50"
          leave="duration-200"
          leaveFrom="opacity-50"
          leaveTo="opacity-0"
        />
        <div className="fixed inset-0 w-screen items-center justify-center overflow-y-auto pt-24 scrollbar mobile:items-start">
          <Transition.Child
            as={Dialog.Panel}
            className={classNames(
              'relative min-h-full rounded-tl-2xl rounded-tr-2xl border-t border-midground4 bg-background',
              activeFilters.length > 0 && 'pb-20'
            )}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <main>
              <div
                className="absolute right-4 top-6 flex h-8 w-8 items-center justify-center rounded-lg border border-midground4"
                onClick={toggleMobileFilterModal}
              >
                <PepIcon iconName="Remove" strokeColor="midground4" />
              </div>
              <EventsFilter
                events={events}
                places={places}
                categoryClasses={categoryClasses}
                eventCategories={eventCategories}
                amenityCategories={amenityCategories}
                filteredEvents={filteredEvents}
                searchText={searchText}
                handleSearchTextChange={handleSearchTextChange}
                unMountInputs={!isMobile}
              />
            </main>
          </Transition.Child>
          {activeFilters.length > 0 && (
            <div className="fixed bottom-0 z-50 bg-background">
              <Transition.Child
                className="flex w-screen flex-row items-center justify-between border-t border-midground3 bg-background px-6 py-7"
                enter="ease-in duration-300"
                enterFrom="translate-y-24"
                enterTo="translate-y-0"
              >
                <button
                  className="font-semibold text-primary hover:text-secondary"
                  onClick={() => clearQueryParams({blacklistKeys: [PANEL_VIEW_SEARCH_PARAM_KEY]})}
                >
                  {uiStrings.general.clearAll}
                </button>
                <button
                  className="w-48 rounded-2xl bg-primary px-4 py-1 text-background"
                  onClick={() => setMobileFiltersOpen(false)}
                >
                  Show{' '}
                  {
                    filteredEvents.filter(event => (panelView === 'grid' ? event.includeInList : event.showOnMap))
                      .length
                  }{' '}
                  events
                </button>
              </Transition.Child>
            </div>
          )}
        </div>
      </Transition>

      {/* vertical line */}
      <div className="sticky top-0 border-l border-midground3"></div>

      {/* results - full width third column */}
      <section className={classNames('flex h-full w-full flex-1 flex-col justify-start scrollbar')}>
        <header className={classNames('z-40 mx-auto flex w-full', activeFilters.length ? 'pb-2' : 'pb-5')}>
          <div className="w-full px-6 md:px-8">
            <div className="mx-auto flex w-full max-w-[82rem] flex-col gap-y-2 pt-5 md:pt-12">
              <div className="flex flex-row items-center justify-between">
                <Heading1 className="pe-2">
                  <span className="font-normal">{uiStrings.general.explore}</span> {uiStrings.events.title}
                </Heading1>
                <div className="flex flex-wrap justify-end gap-x-3 gap-y-2">
                  <SubmitYourEvent />
                  <IconButton
                    label={panelView === 'grid' ? 'Map view' : 'Grid view'}
                    active
                    pepIcon={panelView === 'grid' ? 'Maps' : 'Grid'}
                    onClick={togglePanelView}
                    className="text-light"
                  />
                </div>
              </div>
              <div className="mobile-only flex flex-row gap-x-3 py-2">
                <TextSearchBox
                  className="grow"
                  placeholder={uiStrings.events.searchPlaceholder}
                  text={searchText}
                  onTextChange={handleSearchTextChange}
                  id={'events-search-mobile'}
                  unMountInput={!isMobile}
                />
                <IconButton
                  className="!h-auto px-3"
                  pepIcon="Sliders"
                  label="Filter"
                  active={false}
                  onClick={toggleMobileFilterModal}
                />
              </div>
              {activeFilters.length > 0 && (
                <div className="flex flex-1 flex-row items-center md:pt-3">
                  <div className="flex w-0 flex-grow flex-row gap-x-2 overflow-x-auto scrollbar">
                    {activeFilters.map(filter => (
                      <IconButton
                        aria-label={`Clear filter: ${filter.label}`}
                        key={`filter-badge-${filter.id}`}
                        pepIcon={filter.icon}
                        label={filter.label}
                        active={true}
                        onClick={filter.clear}
                        clearable={true}
                        className={'my-2'}
                      />
                    ))}
                  </div>
                  <button
                    className="ms-2 shrink-0 border-s border-midground4 py-1 ps-4 font-semibold text-primary hover:text-secondary"
                    onClick={() => clearQueryParams({blacklistKeys: [PANEL_VIEW_SEARCH_PARAM_KEY]})}
                  >
                    {uiStrings.general.clearAll}
                  </button>
                </div>
              )}
              {activeFilters.length > 0 && (
                <div className="ms-auto text-sm text-midground4">
                  {
                    filteredEvents.filter(event => (panelView === 'grid' ? event.includeInList : event.showOnMap))
                      .length
                  }{' '}
                  results
                </div>
              )}
            </div>
          </div>
        </header>
        {/* content - map or grid */}
        {panelView === 'grid' && (
          <div className="relative overflow-y-auto pb-5 scrollbar">
            <div className="sticky top-0 z-20 h-[1px] w-full bg-midground3" />
            <div className="relative -top-[1px] z-40 h-[1px] w-full bg-background" />
            <div className="z-20 px-6 md:px-8">
              <div className="mx-auto grid max-w-[82rem] gap-4 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
                {filteredEvents
                  .filter(event => event.includeInList)
                  .map(event => (
                    <EventResult key={`event-result-${event.id}`} event={event} />
                  ))}
              </div>
            </div>
          </div>
        )}

        {panelView === 'map' && (
          <div className="border-3 mx-auto flex h-full w-full flex-grow overflow-hidden px-5 pb-5 md:px-8 md:pb-8">
            <div className="mx-auto flex h-full w-full max-w-[82rem] flex-grow overflow-hidden rounded-2xl border border-midground3">
              <EventsMap
                events={filteredEvents.filter(event => event.showOnMap)}
                eventCategories={eventCategories}
                searchRadius={mapSearchRadius}
              />
            </div>
          </div>
        )}
      </section>
    </div>
  );
};
